import React from 'react';
import BusinessLegalSummary from '../components/layout/BusinessLegalSummary';
import { FileText, Shield, CreditCard, Truck, RotateCcw, AlertCircle, Mail, CheckCircle } from 'lucide-react';

const Terms = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mb-4">
            <FileText className="h-8 w-8 text-orange-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Kullanım Şartları</h1>
          <p className="text-gray-600">
            Son güncelleme: {new Date().toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-lg p-8 md:p-12 space-y-8">
          
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <FileText className="h-6 w-6 text-orange-600 mr-2" />
              1. Giriş ve Kabul
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              LeaserTech web sitesini ("Site") kullanarak, aşağıdaki kullanım şartlarını okuduğunuzu, 
              anladığınızı ve kabul ettiğinizi beyan edersiniz. Bu şartları kabul etmiyorsanız, 
              lütfen Site'yi kullanmayın.
            </p>
            <p className="text-gray-700 leading-relaxed">
              LeaserTech, bu kullanım şartlarını herhangi bir zamanda değiştirme hakkını saklı tutar. 
              Değişiklikler yapıldığında, Site'de yayınlanacak ve yürürlüğe girecektir.
            </p>
          </section>

          {/* Definitions */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              2. Tanımlar
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li><strong>"LeaserTech"</strong> veya <strong>"Biz"</strong>: LeaserTech firmasını ifade eder.</li>
              <li><strong>"Site"</strong>: www.leasertech.com web sitesini ifade eder.</li>
              <li><strong>"Kullanıcı"</strong> veya <strong>"Siz"</strong>: Site'yi ziyaret eden veya kullanan kişiyi ifade eder.</li>
              <li><strong>"Ürün"</strong>: Site üzerinden satışa sunulan tüm ürünleri ifade eder.</li>
              <li><strong>"Hizmet"</strong>: Site üzerinden sunulan tüm hizmetleri ifade eder.</li>
            </ul>
          </section>

          {/* Account */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <Shield className="h-6 w-6 text-orange-600 mr-2" />
              3. Hesap Oluşturma ve Kullanım
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">3.1. Hesap Sorumluluğu</h3>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>Hesap bilgilerinizin doğruluğundan ve güncelliğinden siz sorumlusunuz.</li>
                  <li>Hesap güvenliğinizden ve şifrenizin gizliliğinden siz sorumlusunuz.</li>
                  <li>Hesabınız altında yapılan tüm işlemlerden siz sorumlusunuz.</li>
                  <li>Hesabınızın yetkisiz kullanımından şüpheleniyorsanız, derhal bize bildirmelisiniz.</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">3.2. Yaş Sınırı</h3>
                <p className="text-gray-700 leading-relaxed ml-4">
                  Site'yi kullanmak için en az 18 yaşında olmalısınız veya yasal velinizin izni ile 
                  işlem yapmalısınız. 18 yaşından küçük kullanıcılar, Site'yi yalnızca ebeveyn veya 
                  yasal vasisinin gözetimi altında kullanabilir.
                </p>
              </div>
            </div>
          </section>

          {/* Products and Services */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              4. Ürünler ve Hizmetler
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">4.1. Ürün Bilgileri</h3>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>Site'deki ürün açıklamaları, görselleri ve fiyatları en doğru şekilde güncellemeye çalışıyoruz.</li>
                  <li>Ürün görselleri ve renkler gerçek ürünlerden hafif farklılık gösterebilir.</li>
                  <li>Fiyatlar önceden haber verilmeksizin değiştirilebilir.</li>
                  <li>Stok durumu gerçek zamanlı olarak güncellenir, ancak nadir durumlarda stok tükenmiş olabilir.</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">4.2. Sipariş İşleme</h3>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>Siparişleriniz en kısa sürede işleme alınır.</li>
                  <li>Sipariş onayı e-posta ile bildirilir.</li>
                  <li>Stokta olmayan ürünler için sipariş iptal edilebilir veya ertelenebilir.</li>
                  <li>LeaserTech, sipariş verme hatası, teknik sorun veya stok eksikliği durumunda 
                      siparişi iptal etme hakkını saklı tutar.</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Pricing and Payment */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <CreditCard className="h-6 w-6 text-orange-600 mr-2" />
              5. Fiyatlandırma ve Ödeme
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">5.1. Fiyatlar</h3>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>Tüm fiyatlar Türk Lirası (TRY) cinsinden gösterilir ve KDV dahildir.</li>
                  <li>Fiyatlar önceden haber verilmeksizin değiştirilebilir.</li>
                  <li>Siparişin onaylandığı andaki fiyat geçerlidir.</li>
                  <li>Yanlış fiyatlandırma durumunda, doğru fiyatla iletişime geçilir veya sipariş iptal edilir.</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">5.2. Ödeme Yöntemleri</h3>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>Kredi kartı (Visa, MasterCard) ile güvenli ödeme</li>
                  <li>Banka kartı ile ödeme</li>
                  <li>PaynKolay sanal POS ile ödeme</li>
                  <li>Tüm ödemeler güvenli ödeme altyapısı üzerinden işlenir.</li>
                  <li>Kredi kartı bilgileriniz bizim sunucularımızda saklanmaz.</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Shipping and Delivery */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <Truck className="h-6 w-6 text-orange-600 mr-2" />
              6. Kargo ve Teslimat
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">6.1. Teslimat Süresi</h3>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>Standart kargo: 3-7 iş günü</li>
                  <li>Hızlı kargo: 1-3 iş günü (ek ücret uygulanır)</li>
                  <li>Teslimat süreleri hava durumu, bayram tatilleri gibi olağanüstü durumlardan etkilenebilir.</li>
                  <li>Siparişiniz hazır olduğunda e-posta ile bilgilendirilirsiniz.</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">6.2. Teslimat Adresi</h3>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>Teslimat adresinizin doğru ve güncel olduğundan emin olun.</li>
                  <li>Yanlış adres nedeniyle oluşan ek maliyetler müşteriye aittir.</li>
                  <li>Adres bilgileri yanlışsa veya teslimat yapılamazsa, sipariş iade edilir.</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">6.3. Kargo Ücreti</h3>
                <p className="text-gray-700 leading-relaxed ml-4">
                  Kargo ücretleri sipariş tutarına ve teslimat adresine göre değişiklik gösterir. 
                  Kargo ücreti sipariş onayı sırasında gösterilir.
                </p>
              </div>
            </div>
          </section>

          {/* Returns and Refunds */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <RotateCcw className="h-6 w-6 text-orange-600 mr-2" />
              7. İade ve İptal
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">7.1. İade Hakkı</h3>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>6502 sayılı Tüketicinin Korunması Hakkında Kanun gereğince, ürünü teslim aldığınız 
                      tarihten itibaren 14 gün içinde cayma hakkınız vardır.</li>
                  <li>İade için ürün orijinal ambalajında, kullanılmamış ve hasarsız olmalıdır.</li>
                  <li>Kişisel kullanıma özel ürünler (kulaklık, vb.) sağlık ve hijyen nedeniyle iade edilemez.</li>
                  <li>İade başvurusu için{' '}
                    <a href="/contact" className="text-orange-600 hover:text-orange-700 underline">
                      iletişim
                    </a>{' '}
                    sayfamızdan veya sipariş detaylarınızdan başvuru yapabilirsiniz.
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">7.2. İade İşlemi</h3>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>İade onayından sonra ürünü kargo ile göndermelisiniz.</li>
                  <li>İade kargo ücreti müşteriye aittir (hatalı/eksik ürün durumunda biz karşılarız).</li>
                  <li>Ürün kontrolünden sonra ödeme 14 iş günü içinde iade edilir.</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">7.3. Sipariş İptali</h3>
                <p className="text-gray-700 leading-relaxed ml-4">
                  Sipariş kargoya verilmeden önce iptal edilebilir. Kargoya verildikten sonra 
                  iade prosedürü uygulanır.
                </p>
              </div>
            </div>
          </section>

          {/* User Conduct */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <AlertCircle className="h-6 w-6 text-orange-600 mr-2" />
              8. Kullanıcı Davranışı ve Yasaklar
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Site'yi kullanırken aşağıdaki davranışlar kesinlikle yasaktır:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Site'nin işleyişini bozmak veya zarar vermek</li>
              <li>Yasa dışı amaçlarla Site'yi kullanmak</li>
              <li>Başkalarının hesaplarına yetkisiz erişim sağlamaya çalışmak</li>
              <li>Sahte bilgi vermek veya kimlik hırsızlığı yapmak</li>
              <li>Virüs, zararlı yazılım veya kötü amaçlı kod yüklemek</li>
              <li>Spam, reklam veya istenmeyen içerik göndermek</li>
              <li>Telif hakkı ihlali yapmak</li>
              <li>Diğer kullanıcıları rahatsız etmek veya tehdit etmek</li>
              <li>Site içeriğini kopyalamak, çoğaltmak veya dağıtmak</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              Bu yasakları ihlal eden kullanıcıların hesapları iptal edilebilir ve yasal işlem başlatılabilir.
            </p>
          </section>

          {/* Intellectual Property */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              9. Fikri Mülkiyet Hakları
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Site'nin tüm içeriği (metin, görsel, logo, tasarım, yazılım vb.) LeaserTech'in fikri 
              mülkiyetidir ve telif hakkı, marka hakkı ve diğer fikri mülkiyet yasaları ile korunmaktadır.
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Site içeriğini izinsiz kopyalamak, çoğaltmak veya dağıtmak yasaktır.</li>
              <li>LeaserTech markası ve logosu izinsiz kullanılamaz.</li>
              <li>Site içeriğini ticari amaçlarla kullanmak yasaktır.</li>
            </ul>
          </section>

          {/* Limitation of Liability */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              10. Sorumluluk Sınırlaması
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              LeaserTech, aşağıdaki durumlardan sorumlu tutulamaz:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Site'nin geçici olarak kullanılamaması veya kesintiye uğraması</li>
              <li>Üçüncü taraf hizmetlerindeki aksaklıklar</li>
              <li>Kullanıcı hatası veya yanlış kullanım</li>
              <li>İnternet bağlantı sorunları</li>
              <li>Doğal afetler veya olağanüstü durumlar</li>
              <li>Ürün özelliklerindeki küçük farklılıklar</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              LeaserTech, ürünlerin kullanımından kaynaklanan dolaylı, özel veya sonuç zararlarından 
              sorumlu tutulamaz.
            </p>
          </section>

          {/* Dispute Resolution */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              11. Uyuşmazlık Çözümü
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Bu kullanım şartlarından kaynaklanan uyuşmazlıklar öncelikle görüşmelerle çözülmeye çalışılır. 
              Çözülemezse, Türkiye Cumhuriyeti yasalarına göre çözülecektir.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Uyuşmazlıklar İstanbul Mahkemeleri ve İcra Dairelerinin yetkisi altındadır.
            </p>
          </section>

          {/* Changes */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              12. Değişiklikler
            </h2>
            <p className="text-gray-700 leading-relaxed">
              LeaserTech, bu kullanım şartlarını herhangi bir zamanda değiştirme hakkını saklı tutar. 
              Önemli değişiklikler yapıldığında, Site'de yayınlanacak ve kullanıcılar bilgilendirilecektir. 
              Değişikliklerden sonra Site'yi kullanmaya devam etmeniz, yeni şartları kabul ettiğiniz anlamına gelir.
            </p>
          </section>

          {/* Contact */}
          <section className="bg-orange-50 border-l-4 border-orange-500 p-6 rounded-r-lg">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <Mail className="h-6 w-6 text-orange-600 mr-2" />
              13. İletişim
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Kullanım şartları hakkında sorularınız için bizimle iletişime geçebilirsiniz:
            </p>
            <BusinessLegalSummary className="text-gray-700" />
            <div className="space-y-2 text-gray-700 mt-4">
              <p className="mt-4">
                <a href="/contact" className="text-orange-600 hover:text-orange-700 underline font-medium">
                  İletişim sayfamıza git →
                </a>
              </p>
            </div>
          </section>

          {/* Acceptance */}
          <section className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-lg">
            <div className="flex items-start">
              <CheckCircle className="h-6 w-6 text-green-600 mr-3 mt-1 flex-shrink-0" />
              <div>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                  14. Kabul ve Onay
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  Bu Site'yi kullanarak, yukarıdaki tüm kullanım şartlarını okuduğunuzu, anladığınızı ve 
                  kabul ettiğinizi beyan edersiniz. Bu şartları kabul etmiyorsanız, lütfen Site'yi kullanmayın.
                </p>
              </div>
            </div>
          </section>

        </div>

        {/* Footer Note */}
        <div className="mt-8 text-center text-gray-600 text-sm">
          <p>
            Bu kullanım şartları, Türkiye Cumhuriyeti yasalarına uygun olarak hazırlanmıştır ve 
            6502 sayılı Tüketicinin Korunması Hakkında Kanun ile 6698 sayılı Kişisel Verilerin Korunması 
            Kanunu (KVKK) hükümlerine tabidir.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Terms;





